/**
 * Frontend mirror of backend/src/modules/auth/access-control.ts.
 *
 * MUST stay byte-for-byte equivalent to the backend role definitions.
 * Used only for UI gating (hide/disable affordances). The backend
 * re-checks every request — this is never a security boundary.
 *
 * Per SCPR-005: four roles — Admin, Editor, Estimator, Viewer.
 * "Owner" is a designation on top of Admin (the `isOwner` flag), not
 * a role — gate owner-only UI on the member's isOwner flag instead.
 */
import { createAccessControl } from 'better-auth/plugins/access';
import { defaultStatements } from 'better-auth/plugins/organization/access';

export const statements = {
  ...defaultStatements, // organization, member, invitation

  project:        ['create', 'read', 'update', 'delete', 'archive'],
  module:         ['create', 'read', 'update', 'delete'],
  feature:        ['create', 'read', 'update', 'delete'],
  comment:        ['create', 'read', 'delete', 'deleteAny'],
  attachment:     ['create', 'read', 'delete'],
  status:         ['configure'],
  estimate:       ['create', 'read', 'update', 'delete'],
  estimateColumn: ['rename', 'configurePermissions'],
  productBrief:   ['read', 'update'],
  ai:             ['use'],
  encryption:     ['toggle', 'rotate'],
  marketplace:    ['browse', 'hire', 'sell'],
  billing:        ['read', 'manage'],
  apiKey:         ['create', 'list', 'revoke'],
  auditLog:       ['read', 'export'],
  byok:           ['configure'],
  titleSuggestion: ['create', 'read', 'accept'],
  entityDescription: ['create', 'read', 'select'],
} as const;

export type Statements = typeof statements;

export const ac = createAccessControl(statements);

// Admin (+ Owner via isOwner flag) — full workspace control
export const admin = ac.newRole({
  organization:   ['update', 'delete'],
  member:         ['create', 'update', 'delete'],
  invitation:     ['create', 'cancel'],
  project:        ['create', 'read', 'update', 'delete', 'archive'],
  module:         ['create', 'read', 'update', 'delete'],
  feature:        ['create', 'read', 'update', 'delete'],
  comment:        ['create', 'read', 'delete', 'deleteAny'],
  attachment:     ['create', 'read', 'delete'],
  status:         ['configure'],
  estimate:       ['create', 'read', 'update', 'delete'],
  estimateColumn: ['rename', 'configurePermissions'],
  productBrief:   ['read', 'update'],
  ai:             ['use'],
  encryption:     ['toggle', 'rotate'],
  marketplace:    ['browse', 'hire', 'sell'],
  billing:        ['read', 'manage'],
  apiKey:         ['create', 'list', 'revoke'],
  auditLog:       ['read', 'export'],
  byok:           ['configure'],
  titleSuggestion: ['create', 'read', 'accept'],
  entityDescription: ['create', 'read', 'select'],
});

// Editor — full content authoring; no workspace/billing/members
export const editor = ac.newRole({
  project:        ['create', 'read', 'update'],
  module:         ['create', 'read', 'update', 'delete'],
  feature:        ['create', 'read', 'update', 'delete'],
  comment:        ['create', 'read', 'delete'],   // own comments only — admin gets deleteAny
  attachment:     ['create', 'read', 'delete'],
  estimate:       ['create', 'read', 'update', 'delete'],
  estimateColumn: ['rename'],
  productBrief:   ['read'],
  ai:             ['use'],
  marketplace:    ['browse', 'hire', 'sell'],
  titleSuggestion: ['create', 'read', 'accept'],
  entityDescription: ['create', 'read', 'select'],
});

// Estimator — read content + author estimates only
export const estimator = ac.newRole({
  project:        ['read'],
  module:         ['read'],
  feature:        ['read'],
  comment:        ['create', 'read'],
  attachment:     ['read'],
  estimate:       ['create', 'read', 'update'],
  productBrief:   ['read'],
  ai:             ['use'],
  marketplace:    ['browse', 'sell'],
  titleSuggestion: ['create', 'read'],
  entityDescription: ['create', 'read'],
});

// Viewer — read-only across the workspace
export const viewer = ac.newRole({
  project:        ['read'],
  module:         ['read'],
  feature:        ['read'],
  comment:        ['read'],
  attachment:     ['read'],
  estimate:       ['read'],
  productBrief:   ['read'],
  marketplace:    ['browse', 'sell'],
});

export const roles = { admin, editor, estimator, viewer };
export type RoleName = keyof typeof roles;

/** A permission request: resource → actions. Mirrors the backend shape. */
export type PermissionInput = Partial<{
  [K in keyof Statements]: Array<Statements[K][number]>;
}>;
